/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../api";
import { adminMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/adminMutationKeys";
import { useOtMutation } from "../../hooks/mutations/useOtMutation";
import { IndexTable } from "../index_table";
import camelize from "../../utils/camelize";
import { Loader } from "../loader";
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CloseIcon from '@mui/icons-material/Close';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckIcon from '@mui/icons-material/Check';
import mfaService from "../../api/mfaService";
import { ConfirmationDialog } from "../confirmation_dialog/index.jsx";
import { resetPageAfterDeletion } from '../../utils/resetPageAfterDeletion'
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Box,
  Chip,
  Popover,
  Switch,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import {
  StyledIconContainer,
  StyledIconButton,
  StyledDialogTitle,
  StyledConfirmMessage,
  StyledCheckbox,
  StyledConfirmDeleteContainer,
  StyledConfirmButton,
  StyledCancelButton,
  StyledMfaNotEnrolledChip,
  StyledMfaEnrolledChip,
  StyledMfaEnforcementChip,
  StyledDescription,
  StyledDialogContentText,
  StyledSendButton,
} from "./styles";
import { LoadingSpinner } from '../loading_spinner/index'
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { checkAdmin } from "../../utils/checkAdmin";
import { formatDate } from "../../utils/formatDate";
import { OptionLabel, OptionDescription } from "../mfa_configuration_modal/styles.jsx";

const RenderList = React.memo(({ data }) => {
  return (
    <Box>
      <ul style={{ padding: 0 }}>
        {data?.map((item) => (
          <li key={item + `${Math.random()}`} style={{ listStyle: "none", display: 'inline' }}>
            <Chip
              sx={{
                backgroundColor: 'rgb(206, 231, 254)',
                color: 'rgb(0, 106, 220)',
                "&:hover": {
                  backgroundColor: 'rgb(206, 231, 254)',
                  color: 'rgb(0, 106, 220)',
                },
                fontWeight: 500,
                marginRight: '3px'
              }}
              label={item}
            />
          </li>
        ))}
      </ul>
    </Box>
  );
});

RenderList.displayName = 'RenderList'

const RenderInstanceLevelRolesList = React.memo(({ data }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [visibleCards, setVisibleCards] = useState([]);
  const [hiddenCards, setHiddenCards] = useState([]);
  const containerRef = useRef(null);
  const hoverTimeout = useRef(null);

  const calculateVisibleCards = useCallback(() => {
    if (!containerRef.current || !data?.length) return;

    const container = containerRef.current;
    const containerWidth = container.offsetWidth;

    const cardWidth = 180;
    const plusButtonWidth = 40;

    const newVisibleCards = [];
    const newHiddenCards = [];

    let maxVisible = 0;
    for (let i = 0; i < data.length; i++) {
      const requiredWidth = (i + 1) * cardWidth + (i < data.length - 1 ? plusButtonWidth : 0);
      if (requiredWidth <= containerWidth) {
        maxVisible = i + 1;
      } else {
        break;
      }
    }

    if (maxVisible === 0 && data.length > 0) {
      setVisibleCards([]);
      setHiddenCards(data);
      return;
    }

    const finalVisibleCount = Math.min(maxVisible, data.length);

    data.forEach((card, index) => {
      if (index < finalVisibleCount) {
        newVisibleCards.push(card);
      } else {
        newHiddenCards.push(card);
      }
    });

    setVisibleCards(newVisibleCards);
    setHiddenCards(newHiddenCards);
  }, [data]);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleCards();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [calculateVisibleCards]);

  useEffect(() => {
    calculateVisibleCards();
  }, [data, calculateVisibleCards]);

  const handleOpenPopover = (event) => {
    if (hiddenCards?.length > 0) {
      clearTimeout(hoverTimeout.current);
      setAnchorEl(event.currentTarget);
      setIsPopoverOpen(true);
    }
  };

  const handleClosePopover = () => {
    hoverTimeout.current = setTimeout(() => {
      setAnchorEl(null);
      setIsPopoverOpen(false);
    }, 150);
  };

  const handlePopoverMouseEnter = () => {
    clearTimeout(hoverTimeout.current);
    setIsPopoverOpen(true);
  };

  const handlePopoverMouseLeave = () => {
    handleClosePopover();
  };

  const open = Boolean(anchorEl) && isPopoverOpen;

  if (!data || !Array.isArray(data) || data?.length === 0) {
    return '';
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        minWidth: '50px',
        width: '100%',
        margin: '16px 3px 16px 0px'
      }}
    >
      {data?.map((item, index) => (
        <Chip
          key={index}
          sx={{
            backgroundColor: 'rgb(206, 231, 254)',
            color: 'rgb(0, 106, 220)',
            "&:hover": {
              backgroundColor: 'rgb(206, 231, 254)',
              color: 'rgb(0, 106, 220)',
            },
            marginRight: '3px',
            display: visibleCards.includes(item) ? 'inline-flex' : 'none',
          }}
          label={item}
          onMouseEnter={handleOpenPopover}
        />
      ))}

      {hiddenCards.length > 0 && (
        <IconButton
          onMouseEnter={handleOpenPopover}
          sx={{
            height: '25px',
            width: '25px',
            backgroundColor: 'lightgray',
            "&:hover": {
              backgroundColor: 'lightgray'
            },
            flexShrink: 0,
            minWidth: '25px'
          }}
        >
          <Typography sx={{ fontSize: '14px' }}>
            {`+${hiddenCards.length}`}
          </Typography>
        </IconButton>
      )}

      <Popover
        id="mouse-over-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: '150px',
              borderRadius: '8px',
              overflow: 'hidden',
              marginTop: '5px'
            },
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handlePopoverMouseLeave
          }
        }}
        disableRestoreFocus
      >
        <Box sx={{ padding: '16px' }}>
          {hiddenCards?.length > 0 && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {hiddenCards?.map((item, index) => (
              <Chip
                key={index}
                sx={{
                  backgroundColor: 'rgb(206, 231, 254)',
                  color: 'rgb(0, 106, 220)',
                }}
                label={item}
              />
            ))}
          </Box>}
        </Box>
      </Popover>
    </Box>
  );
});

RenderInstanceLevelRolesList.displayName = 'RenderInstanceLevelRolesList'

export const RestaurantAdminsTable = (
  { open,
    data,
    itemsPerPage,
    setItemsPerPage,
    page,
    setPage,
    pageCount,
    fetchingLoader,
    edit,
    setEdit,
    setUpdateAdmin,
    setEditError,
    setEditRoles,
    setFetch,
    tab,
    showDownloadButton,
    disableDownloadButton,
    handleExportCsv,
    csvLoading,
    showNotification,
    sortBy,
    sortOrder,
    onSortChange,
    searchLoading,
    hasSearchTerm,
  }) => {
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [restoreOpen, setRestoreOpen] = useState(false)
  const [purgeOpen, setPurgeOpen] = useState(false)
  const [deleteAdmin, setDeleteAdmin] = useState({})
  const [deleteConfirm, setDeleteConfirm] = useState(true)
  const [restoreConfirm, setRestoreConfirm] = useState(true)
  const [purgeConfirm, setPurgeConfirm] = useState(true)
  const [deleteError, setDeleteError] = useState(false)
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('')
  const [mfaTogglingId, setMfaTogglingId] = useState(null)
  const [mfaResetOpen, setMfaResetOpen] = useState(false)
  const [mfaResetAdmin, setMfaResetAdmin] = useState(null)
  const [mfaResetConfirm, setMfaResetConfirm] = useState(true)
  const [mfaEnforcementDialogOpen, setMfaEnforcementDialogOpen] = useState(false)
  const [mfaEnforcementSelected, setMfaEnforcementSelected] = useState('mandatory')
  const [mfaPendingAdminId, setMfaPendingAdminId] = useState(null)
  const [mfaDisableDialogOpen, setMfaDisableDialogOpen] = useState(false)
  const [mfaDisablePendingAdmin, setMfaDisablePendingAdmin] = useState(null)
  const [selectedAdmin, setSelectedAdmin] = useState({})
  const [sendOpen, setSendOpen] = useState(false)
  const [sendEmailType, setSendEmailType] = useState('')
  const [emailLoading, setEmailLoading] = useState(false)


  const [status, setStatus] = useState(false)
  const { admin } = useSelector((state) => state.admin);
  const superAdmin = checkAdmin(admin?.formatted_roles)
  const queryClient = useQueryClient();

  const isQuarantined = tab === '2';

  const TableColumns = useMemo(() => {
    const baseColumns = [
      { key: "id", sortable: true },
      { key: "first_name", sortable: true },
      { key: "last_name", sortable: true },
      { key: "username", sortable: true },
      { key: "phone_no", sortable: true },
      { key: "email", sortable: true },
    ];

    if (isQuarantined) {
      baseColumns.push(
        { key: "performed_by", sortable: true },
        { key: "performed_at", sortable: true }
      )
    }

    if (!isQuarantined) {
      baseColumns.push(
        { key: "last_login", sortable: true },
        { key: "top_level_role", sortable: true },
        { key: "instance_level_role", sortable: true },
        { key: "mfa_enabled", sortable: true },
        { key: "mfa_enrolled", sortable: true }
      );
    }

    baseColumns.push({ key: "actions", sortable: false, minWidth: 230 });
    return baseColumns;
  }, [isQuarantined]);

  const columns = useMemo(() =>
    TableColumns?.map((column) => ({
      key: column.key,
      label: camelize(column.key),
      sortable: column.sortable,
      minWidth: column.minWidth
    })), [TableColumns]
  );

  const handleDeleteAlert = useCallback((admin) => {
    setDeleteOpen(true)
    setDeleteAdmin(admin)
  }, [])

  const handleDeleteClose = useCallback(() => {
    setDeleteOpen(false)
    setDeleteConfirm(true)
  }, [])

  const handleRestoreAlert = useCallback((admin) => {
    setRestoreOpen(true)
    setDeleteAdmin(admin)
    setRestoreConfirm(true)
  }, [])

  const handleRestoreClose = useCallback(() => {
    setRestoreOpen(false)
    setRestoreConfirm(true)
  }, [])

  const handlePurgeAlert = useCallback((admin) => {
    setPurgeOpen(true)
    setDeleteAdmin(admin)
    setPurgeConfirm(true)
  }, [])

  const handlePurgeClose = useCallback(() => {
    setPurgeOpen(false)
    setPurgeConfirm(true)
  }, [])

  const handleEdit = async (admin) => {
    setUpdateAdmin({})
    setEdit(true)
    setFetch(true)

    const data = await getAdminData(admin.id);
    setUpdateAdmin(data)
    setEditRoles(data?.formatted_roles)

    setEditError({
      first_name: '',
      last_name: '',
      phone_no: '',
      email: '',
      username: ''
    })
  }

  const getAdminData = async (id) => {
    const data = await api
      .get(`/v1/admin/admins/${id}`)
      .then((response) => response.data);

    return data;
  };

  const checkTotalPages = () => {
    resetPageAfterDeletion({ totalCount: pageCount, removed: 1, page, itemsPerPage, setPage })
  }

  const { mutateAsync: deleteAdminMutation } = useOtMutation({
    mutationFn: async (id) => await api.delete(`/v1/admin/admins/${id}`),
    invalidateKeys: adminMutationKeyGroups.delete,
    successMessage: 'User quarantined successfully',
    onError: (error) => {
      // Check if it's a last Super Admin error
      const errorMessage = error?.response?.data?.errors?.base?.[0] ||
        error?.response?.data?.errors?.base ||
        'You dont have permissions to delete users';
      setDeleteErrorMessage(errorMessage);
      setDeleteError(true);
    },
    errorMessage: () => null,
    onSettled: (_data, error) => {
      setDeleteOpen(false)
      setDeleteConfirm(true)
      setStatus(false)

      if (!error) {
        checkTotalPages()
      }
    }
  });

  const handleDelete = useCallback(async () => {
    setStatus(true)
    try {
      await deleteAdminMutation(deleteAdmin?.id)
    } catch (_error) {
      // Errors are handled by onError via inline snackbar state.
    }
  }, [deleteAdmin, deleteAdminMutation])

  const { mutateAsync: restoreAdminMutation } = useOtMutation({
    mutationFn: async (id) => await api.post(`/v1/admin/admins/${id}/restore`),
    invalidateKeys: adminMutationKeyGroups.update,
    successMessage: 'User restored successfully',
    onError: (error) => {
      // Extract error message from API response
      const errorMessage = error?.response?.data?.errors?.base?.[0] ||
        error?.response?.data?.errors?.base ||
        'Failed to restore user';
      setDeleteErrorMessage(errorMessage);
      setDeleteError(true);
    },
    errorMessage: () => null,
    onSettled: (_data, error) => {
      setRestoreOpen(false)
      setRestoreConfirm(true)
      setStatus(false)

      if (!error) {
        checkTotalPages()
      }
    }
  });

  const handleRestore = useCallback(async () => {
    setStatus(true)
    await restoreAdminMutation(deleteAdmin?.id)
  }, [deleteAdmin, restoreAdminMutation]);

  const { mutateAsync: purgeAdminMutation } = useOtMutation({
    mutationFn: async (id) => await api.delete(`/v1/admin/admins/${id}/purge`),
    invalidateKeys: adminMutationKeyGroups.delete,
    successMessage: 'User permanently deleted',
    onError: (error) => {
      const errorMessage = error?.response?.data?.errors?.base?.[0] ||
        error?.response?.data?.errors?.base ||
        'Failed to permanently delete user';
      setDeleteErrorMessage(errorMessage);
      setDeleteError(true);
    },
    errorMessage: () => null,
    onSettled: (_data, error) => {
      setPurgeOpen(false)
      setPurgeConfirm(true)
      setStatus(false)

      if (!error) {
        checkTotalPages()
      }
    }
  });

  const handlePurge = useCallback(async () => {
    setStatus(true)
    await purgeAdminMutation(deleteAdmin?.id)
  }, [deleteAdmin, purgeAdminMutation])

  const handleMfaToggle = async (adminId, currentEnabled, currentEnforcement, adminData) => {
    if (!(superAdmin?.role === 'admin')) return;

    const nextEnabled = !currentEnabled;

    if (!nextEnabled) {
      setMfaDisablePendingAdmin(adminData);
      setMfaDisableDialogOpen(true);
      return;
    }

    setMfaPendingAdminId(adminId);
    setMfaEnforcementSelected(currentEnforcement || 'mandatory');
    setMfaEnforcementDialogOpen(true);
  }

  const { mutateAsync: mfaDisableMutation, isPending: isMfaDisablePending } = useOtMutation({
    mutationFn: async (id) => await mfaService.toggleMFAForAdmin(id, false, undefined),
    invalidateKeys: adminMutationKeyGroups.update,
    successMessage: ({ data }) => data?.message || 'MFA disabled successfully',
    onError: (error) => {
      const errorMessage = error?.response?.data?.error || 'Failed to toggle MFA';
      setDeleteErrorMessage(errorMessage);
      setDeleteError(true);
    },
    errorMessage: () => null,
    onSettled: () => {
      setMfaTogglingId(null);
      setMfaDisablePendingAdmin(null);
    }
  });

  const handleMfaDisableConfirm = async () => {
    if (!mfaDisablePendingAdmin) return;

    setMfaTogglingId(mfaDisablePendingAdmin.id);
    setMfaDisableDialogOpen(false);

    await mfaDisableMutation(mfaDisablePendingAdmin.id);
  }

  const handleMfaDisableCancel = () => {
    setMfaDisableDialogOpen(false);
    setMfaDisablePendingAdmin(null);
  }

  const { mutateAsync: mfaEnforcementConfirmMutation } = useOtMutation({
    mutationFn: async ({ id, enforcement }) => await mfaService.toggleMFAForAdmin(id, true, enforcement),
    invalidateKeys: adminMutationKeyGroups.update,
    successMessage: ({ data }) => data?.message || 'MFA enabled successfully',
    onError: (error) => {
      const errorMessage = error?.response?.data?.error || 'Failed to toggle MFA';
      setDeleteErrorMessage(errorMessage);
      setDeleteError(true);
    },
    errorMessage: () => null,
    onSettled: () => {
      setMfaTogglingId(null);
      setMfaPendingAdminId(null);
    }
  });

  const handleMfaEnforcementConfirm = async () => {
    if (!mfaPendingAdminId) return;

    setMfaTogglingId(mfaPendingAdminId);
    setMfaEnforcementDialogOpen(false);

    await mfaEnforcementConfirmMutation({ id: mfaPendingAdminId, enforcement: mfaEnforcementSelected });
  }

  const handleMfaEnforcementCancel = () => {
    setMfaEnforcementDialogOpen(false);
    setMfaPendingAdminId(null);
    setMfaEnforcementSelected('mandatory');
  }

  const { mutateAsync: mfaEnforcementChangeMutation } = useOtMutation({
    mutationFn: async ({ id, enforcement }) => await mfaService.toggleMFAForAdmin(id, true, enforcement),
    invalidateKeys: adminMutationKeyGroups.update,
    successMessage: ({ data }) => {
      const nextEnforcement = data?.mfa_enforcement;

      if (nextEnforcement) {
        const formattedEnforcement = nextEnforcement === 'optional' ? 'Optional' : 'Mandatory';
        return `MFA enrollment updated to ${formattedEnforcement}`;
      }

      return data?.message || 'MFA enforcement updated successfully';
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.error || 'Failed to update MFA enforcement';
      setDeleteErrorMessage(errorMessage);
      setDeleteError(true);
    },
    errorMessage: () => null,
    onSettled: () => {
      setMfaTogglingId(null);
    }
  });

  const handleMfaEnforcementChange = async (adminId, nextEnforcement) => {
    if (!(superAdmin?.role === 'admin')) return;

    setMfaTogglingId(adminId);

    await mfaEnforcementChangeMutation({ id: adminId, enforcement: nextEnforcement });
  }

  const handleMfaResetAlert = (admin) => {
    setMfaResetOpen(true);
    setMfaResetAdmin(admin);
    setMfaResetConfirm(true);
  }

  const handleMfaResetClose = () => {
    setMfaResetOpen(false);
    setMfaResetConfirm(true);
  }

  const { mutateAsync: mfaResetMutation } = useOtMutation({
    mutationFn: async (id) => await mfaService.resetAdminMFA(id),
    invalidateKeys: adminMutationKeyGroups.update,
    successMessage: ({ data }) => data?.message || 'MFA reset successfully',
    onError: (error) => {
      const errorMessage = error?.response?.data?.error || 'Failed to reset MFA';
      setDeleteErrorMessage(errorMessage);
      setDeleteError(true);
    },
    errorMessage: () => null,
    onSettled: () => {
      setMfaResetOpen(false);
      setMfaResetConfirm(true);
      setStatus(false);
    }
  });

  const handleMfaReset = async () => {
    setStatus(true);
    await mfaResetMutation(mfaResetAdmin?.id);
  }

  const handleSendEmailConfirm = (admin) => {
    setSelectedAdmin(admin)
    setSendOpen(true)
    setSendEmailType(admin?.password_changed ? 'resend' : 'send')
  }

  const handleMfaEnrolledSortValue = (record) => {
    if (!record?.mfa_enabled) return 'Not Enrolled'
    if (record?.mfa_enrolled) {
      return 'Enrolled'
    }
    else {
      return record?.mfa_enforcement === 'optional' ? 'Optional' : 'Mandatory'
    }
  }

  const rows = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    return data?.map((record) => {
      return {
        id: record?.id,
        first_name: record?.first_name,
        last_name: record?.last_name,
        username: record?.username,
        phone_no: Number(record?.phone_no),
        email: record?.email,
        ...(isQuarantined && {
          performed_by: record?.deleted_by_name,
          performed_at: record?.deleted_at ? formatDate(record?.deleted_at, record?.time_zone_abbr) : '',
        }),
        ...(!isQuarantined && {
          last_login: record?.last_login ? formatDate(record?.last_login, record?.time_zone_abbr) : '',
          top_level_role: {
            display: (
              React.createElement(RenderList, { data: record?.roles?.top_level })
            ),
            sortValue: record?.roles?.top_level?.[0]
          },
          instance_level_role: {
            display: (
              React.createElement(RenderInstanceLevelRolesList, { data: record?.roles?.instance_level })
            ),
            sortValue: (
              record?.roles?.instance_level?.length
            )
          },
          mfa_enabled: (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={record?.mfa_enabled || false}
                onChange={() => handleMfaToggle(record?.id, record?.mfa_enabled, record?.mfa_enforcement, record)}
                disabled={!(superAdmin?.role === 'admin') || (mfaTogglingId === record?.id && isMfaDisablePending)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#395bbd',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#395bbd',
                  },
                }}
              />
            </Box>
          ),
          mfa_enrolled: {
            display: (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!record?.mfa_enabled ? (
                <StyledMfaNotEnrolledChip
                  label="Not Enrolled"
                  size="small"
                />
              ) : (
                <>
                  {(() => {
                    if (record?.mfa_enrolled) {
                      return (
                        <StyledMfaEnrolledChip
                          label="Enrolled"
                          size="small"
                        />
                      );
                    } else {
                      const isOptional = record?.mfa_enforcement === 'optional';
                      const label = isOptional ? 'Optional' : 'Mandatory';
                      const hoverText = isOptional ? 'Change to mandatory' : 'Change to optional';

                      return (
                        <Tooltip title={hoverText} arrow>
                          <span>
                            <StyledMfaEnforcementChip
                              optional={isOptional}
                              label={label}
                              size="small"
                              onClick={() => {
                                if (!(superAdmin?.role === 'admin')) return;
                                const next = isOptional ? 'mandatory' : 'optional';
                                handleMfaEnforcementChange(record?.id, next);
                              }}
                            />
                          </span>
                        </Tooltip>
                      );
                    }
                  })()}

                  {record?.mfa_enrolled && (
                    <Tooltip title='Reset MFA' arrow>
                      <span onClick={() => superAdmin?.role === 'admin' && handleMfaResetAlert(record)}>
                        <IconButton
                          data-testid='mfa-reset-button'
                          size="small"
                          disabled={!(superAdmin?.role === 'admin')}
                        >
                          <LockResetIcon
                            sx={{
                              fontSize: 18,
                              color: !(superAdmin?.role === 'admin') ? '#e0e0e0' : '#ff9800',
                              cursor: 'pointer',
                            }}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>
          ),
          sortValue: handleMfaEnrolledSortValue(record)
          },
        }),
        actions: record?.deleted_at ? (
          <div style={{ display: 'flex', gap: '2px' }}>
            <Tooltip title='Restore' arrow>
              <span onClick={() => superAdmin?.role === 'admin' && handleRestoreAlert(record)}>
                <IconButton data-testid='restore-button' disabled={!(superAdmin?.role === 'admin')}>
                  <RestoreIcon
                    sx={{
                      color: !(superAdmin?.role === 'admin') ? '#e0e0e0' : '#4caf50',
                      cursor: 'pointer',
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title='Purge (Permanent Delete)' arrow>
              <span onClick={() => superAdmin?.role === 'admin' && handlePurgeAlert(record)}>
                <IconButton data-testid='purge-button' disabled={!(superAdmin?.role === 'admin')}>
                  <DeleteForeverIcon
                    sx={{
                      color: !(superAdmin?.role === 'admin') ? '#e0e0e0' : '#d32f2f',
                      cursor: 'pointer',
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
            <Tooltip title='Edit' arrow>
              <span onClick={() => superAdmin?.role === 'admin' && handleEdit(record)}>
                <IconButton data-testid='edit-button' disabled={!(superAdmin?.role === 'admin')}>
                  <ModeEditIcon
                    sx={{
                      color: !(superAdmin?.role === 'admin') ? '#e0e0e0' : '#707070',
                      cursor: 'pointer',
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title='Quarantine' arrow>
              <span onClick={() => superAdmin?.role === 'admin' && handleDeleteAlert(record)}>
                <IconButton data-testid='delete-button' disabled={!(superAdmin?.role === 'admin')}>
                  <DeleteIcon
                    sx={{
                      color: !(superAdmin?.role === 'admin') ? '#e0e0e0' : '#707070',
                      cursor: 'pointer',
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            <StyledSendButton
              data-testid='send-email-btn'
              variant="contained"
              disableRipple
              onClick={() => handleSendEmailConfirm(record)}
              disabled={!(superAdmin?.role === 'admin')}
            >
              {record?.password_changed ? 'Resend email' : 'Send email'}
            </StyledSendButton>
          </div>
        )
      }
    });
  }, [data]);

  const sendEmailDialogClose = () => {
    setSendOpen(false)
    setSelectedAdmin({})
    setSendEmailType('')
  }

  const handleSendEmail = async () => {
    try {
      setEmailLoading(true)
      await api.post('/v1/admin/send_credential_email', { email: selectedAdmin?.email })
      showNotification('Email sent successfully', 'success')
    } catch {
      showNotification('Failed to send an email', 'error')
    } finally {
      setEmailLoading(false)
      sendEmailDialogClose()
    }
  }

  return (
    <React.Fragment>
      <Dialog
        open={deleteOpen}
        onClose={handleDeleteClose}
        maxWidth="xs"
      >
        <StyledIconContainer>
          <StyledIconButton disableRipple>
            <CloseIcon sx={{ color: '#ff4c00', fontSize: '30px' }} />
          </StyledIconButton>
        </StyledIconContainer>

        <StyledDialogTitle>Are you sure you want to quarantine this user?</StyledDialogTitle>

        <DialogContent>
          <StyledDialogContentText sx={{ fontSize: '15px', color: '#535353' }}>
            This user will be moved to quarantine and blocked from access.
            <span style={{ fontWeight: '600' }}>
              {` '${deleteAdmin?.first_name} `}
            </span>
            <span style={{ fontWeight: '600' }}>
              {`${deleteAdmin?.last_name}' `}
            </span>
            can be restored within the quarantine period.
          </StyledDialogContentText>

          <StyledConfirmDeleteContainer>
            <StyledCheckbox
              disableRipple
              size="small"
              onClick={() => setDeleteConfirm(!deleteConfirm)}
            />
            <StyledConfirmMessage>Yes, I want to quarantine this user.</StyledConfirmMessage>
          </StyledConfirmDeleteContainer>
        </DialogContent>

        <DialogActions sx={{ marginBottom: '3%' }}>
          <StyledCancelButton onClick={handleDeleteClose}>Cancel</StyledCancelButton>
          <StyledConfirmButton
            variant="contained"
            disabled={deleteConfirm || status}
            onClick={handleDelete}
          >
            {status ? <LoadingSpinner isLoading={true} /> : 'Confirm'}
          </StyledConfirmButton>
        </DialogActions>
      </Dialog>


      <Dialog
        open={restoreOpen}
        onClose={handleRestoreClose}
        maxWidth="xs"
      >
        <StyledIconContainer>
          <StyledIconButton disableRipple>
            <RestoreIcon sx={{ color: '#ff4c00', fontSize: '30px' }} />
          </StyledIconButton>
        </StyledIconContainer>

        <StyledDialogTitle>Are you sure you want to restore this user?</StyledDialogTitle>

        <DialogContent>
          <StyledDialogContentText>
            This will restore
            <span style={{ fontWeight: '600' }}>
              {` '${deleteAdmin?.first_name} `}
            </span>
            <span style={{ fontWeight: '600' }}>
              {`${deleteAdmin?.last_name}' `}
            </span>
            and re-apply their original roles and permissions.
          </StyledDialogContentText>

          <StyledConfirmDeleteContainer>
            <StyledCheckbox
              disableRipple
              size="small"
              onClick={() => setRestoreConfirm(!restoreConfirm)}
            />
            <StyledConfirmMessage>Yes, I want to restore this user.</StyledConfirmMessage>
          </StyledConfirmDeleteContainer>
        </DialogContent>

        <DialogActions sx={{ marginBottom: '3%' }}>
          <StyledCancelButton onClick={handleRestoreClose}>Cancel</StyledCancelButton>
          <StyledConfirmButton
            variant="contained"
            disabled={restoreConfirm || status}
            onClick={handleRestore}
          >
            {status ? <LoadingSpinner isLoading={true} /> : 'Confirm'}
          </StyledConfirmButton>
        </DialogActions>
      </Dialog>

      {/* Purge Dialog */}
      <Dialog
        open={purgeOpen}
        onClose={handlePurgeClose}
        maxWidth="xs"
      >
        <StyledIconContainer>
          <StyledIconButton disableRipple>
            <CloseIcon sx={{ color: '#ff4c00', fontSize: '30px' }} />
          </StyledIconButton>
        </StyledIconContainer>

        <StyledDialogTitle>Are you sure you want to permanently delete this user?</StyledDialogTitle>

        <DialogContent>
          <StyledDialogContentText>
            This action will permanently delete
            <span style={{ fontWeight: '600' }}>
              {` '${deleteAdmin?.first_name} `}
            </span>
            <span style={{ fontWeight: '600' }}>
              {`${deleteAdmin?.last_name}' `}
            </span>
            from the system completely.
          </StyledDialogContentText>

          <StyledConfirmDeleteContainer>
            <StyledCheckbox
              disableRipple
              size="small"
              onClick={() => setPurgeConfirm(!purgeConfirm)}
            />
            <StyledConfirmMessage>Yes, I want to permanently delete this user.</StyledConfirmMessage>
          </StyledConfirmDeleteContainer>
        </DialogContent>

        <DialogActions sx={{ marginBottom: '3%' }}>
          <StyledCancelButton onClick={handlePurgeClose}>Cancel</StyledCancelButton>
          <StyledConfirmButton
            variant="contained"
            disabled={purgeConfirm || status}
            onClick={handlePurge}
          >
            {status ? <LoadingSpinner isLoading={true} /> : 'Confirm'}
          </StyledConfirmButton>
        </DialogActions>
      </Dialog>

      {/* Send email dialog */}
      <ConfirmationDialog
        dataTestId={'send-email-dialog'}
        open={sendOpen}
        onClose={sendEmailDialogClose}
        Icon={CheckIcon}
        dialogTitle={`Are you sure you want to ${sendEmailType} an email to ${selectedAdmin?.first_name} ?`}
        dialogBody={
          <React.Fragment>
            {
              sendEmailType === 'send'
              ? `This will send ${selectedAdmin?.first_name}'s email and password`
              : `This will send ${selectedAdmin?.first_name}'s email`
            }
          </React.Fragment>
        }
        confirmationText={'Yes, I want to send the email'}
        handleConfirm={handleSendEmail}
        isLoading={emailLoading}
      />

      {/* MFA Reset Dialog */}
      {/* MFA Enforcement Selection Dialog */}
      <Dialog
        open={mfaEnforcementDialogOpen}
        onClose={handleMfaEnforcementCancel}
        maxWidth="xs"
      >
        <StyledDialogTitle>Enable MFA</StyledDialogTitle>

        <DialogContent>
          <StyledDescription variant="body2">
            Choose how users will be required to set up MFA:
          </StyledDescription>

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={mfaEnforcementSelected}
              onChange={(e) => setMfaEnforcementSelected(e.target.value)}
            >
              <FormControlLabel
                value="mandatory"
                control={
                  <Radio
                    sx={{
                      '&.Mui-checked': {
                        color: '#395bbd',
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <OptionLabel variant="body2">
                      Mandatory
                    </OptionLabel>
                    <OptionDescription variant="caption" color="text.secondary">
                      User must enroll MFA immediately on next login
                    </OptionDescription>
                  </Box>
                }
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                value="optional"
                control={
                  <Radio
                    sx={{
                      '&.Mui-checked': {
                        color: '#395bbd',
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <OptionLabel variant="body2">
                      Optional
                    </OptionLabel>
                    <OptionDescription variant="caption" color="text.secondary">
                      User can skip enrollment and set it up later
                    </OptionDescription>
                  </Box>
                }
                sx={{ alignItems: 'flex-start' }}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ marginBottom: '3%' }}>
          <StyledCancelButton onClick={handleMfaEnforcementCancel}>
            Cancel
          </StyledCancelButton>
          <StyledConfirmButton
            variant="contained"
            onClick={handleMfaEnforcementConfirm}
            disabled={mfaTogglingId !== null}
          >
            {mfaTogglingId !== null ? <LoadingSpinner isLoading={true} /> : 'Enable MFA'}
          </StyledConfirmButton>
        </DialogActions>
      </Dialog>

      {/* MFA Disable Confirmation Dialog */}
      <ConfirmationDialog
        open={mfaDisableDialogOpen}
        onClose={handleMfaDisableCancel}
        Icon={CheckIcon}
        dialogTitle={'Are you sure you want to disable MFA? This change will apply immediately.'}
        dialogBody={<React.Fragment>
          {'This action will disable MFA for'}
          <span style={{ fontWeight: "600" }}>
            {` '${mfaDisablePendingAdmin?.first_name} ${mfaDisablePendingAdmin?.last_name}'`}
          </span>
        </React.Fragment>}
        confirmationText={'Yes, I want to disable MFA'}
        handleConfirm={handleMfaDisableConfirm}
        isLoading={mfaTogglingId === mfaDisablePendingAdmin?.id}
      />

      {/* MFA Reset Dialog */}
      <Dialog
        open={mfaResetOpen}
        onClose={handleMfaResetClose}
        maxWidth="xs"
      >
        <StyledIconContainer>
          <StyledIconButton disableRipple>
            <LockResetIcon sx={{ color: '#ff9800', fontSize: '30px' }} />
          </StyledIconButton>
        </StyledIconContainer>

        <StyledDialogTitle>Reset MFA for this user?</StyledDialogTitle>

        <DialogContent>
          <StyledDialogContentText sx={{ fontSize: '15px', color: '#535353' }}>
            This will reset MFA enrollment for
            <span style={{ fontWeight: '600' }}>
              {` '${mfaResetAdmin?.first_name} `}
            </span>
            <span style={{ fontWeight: '600' }}>
              {`${mfaResetAdmin?.last_name}' `}
            </span>
            and they will need to re-enroll their passkey on next login.
          </StyledDialogContentText>

          <StyledConfirmDeleteContainer>
            <StyledCheckbox
              disableRipple
              size="small"
              data-testid='mfa-reset-confirm-checkbox'
              onClick={() => setMfaResetConfirm(!mfaResetConfirm)}
            />
            <StyledConfirmMessage>Yes, I want to reset MFA for this user.</StyledConfirmMessage>
          </StyledConfirmDeleteContainer>
        </DialogContent>

        <DialogActions sx={{ marginBottom: '3%' }}>
          <StyledCancelButton onClick={handleMfaResetClose}>Cancel</StyledCancelButton>
          <StyledConfirmButton
            variant="contained"
            disabled={mfaResetConfirm || status}
            onClick={handleMfaReset}
          >
            {status ? <LoadingSpinner isLoading={true} /> : 'Confirm'}
          </StyledConfirmButton>
        </DialogActions>
      </Dialog>

      {/* Error Alert */}
      <Snackbar
        open={deleteError}
        onClose={() => setDeleteError(false)}
        autoHideDuration={6000}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={() => setDeleteError(false)}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {deleteErrorMessage}
        </Alert>
      </Snackbar>

      <div style={{ width: open || edit ? 'calc(100% - 570px)' : '100%' }}>
        {
          fetchingLoader ? (
            <Loader />
          ) : (
            <IndexTable
              report={false}
              columns={columns}
              data={rows}
              tableId="users"
              type="entries"
              sortMode="server"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={onSortChange}
              searchLoading={searchLoading}
              hasSearchTerm={hasSearchTerm}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              page={page}
              setPage={setPage}
              pageCount={pageCount}
              showDownloadButton={showDownloadButton}
              disableDownloadButton={disableDownloadButton}
              handleExportCsv={handleExportCsv}
              csvLoading={csvLoading}
              emptyStateText={"No Data Exists"}
            />
          )
        }
      </div>
    </React.Fragment>
  )
}
